import { useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useTable } from "react-table";
import { toast } from "react-toastify";

import { deleteScheduledReport } from "api/services/user";
import type { ScheduledReport } from "api/models";
import { useGetScheduledReports } from "api/queries/user/scheduledReports/useGetScheduledReports";
import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/CircleCross.svg";
import Button from "components/atoms/Button/Button";
import ConfirmationDialog from "components/organisms/ConfirmationDialog/ConfirmationDialog";
import DataTable from "components/organisms/DataTable/DataTable";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import ScheduledReportDialog from "./ScheduledReportDialog";
import { getScheduledReportList } from "./scheduledReportHelpers";
import "./ScheduledReports.scss";

function ScheduledReports() {
  const intl = useIntl();
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { data, isLoading, refetch } = useGetScheduledReports();
  const reports = useMemo(() => getScheduledReportList(data), [data]);

  const handleDelete = () => {
    if (!deleteId) return;
    deleteScheduledReport(deleteId)
      .then(() => {
        refetch();
        setDeleteId(null);
        toast(
          intl.formatMessage({
            id: "report.successfullyDeleted",
            defaultMessage: "Report successfully deleted.",
          }),
          { type: "success" },
        );
      })
      .catch((error) => {
        setDeleteId(null);
        errorToast(
          error,
          intl.formatMessage({
            id: "report.couldNotDeleteError",
            defaultMessage: "Report could not be deleted. Please try again later.",
          }),
        );
      });
  };

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: "report.title", defaultMessage: "Title" }),
        accessor: "report_name",
        sortable: false,
        disableDrag: true,
        Cell: ({ value }: { value: string }) => <p className="mb-0">{value}</p>,
      },
      {
        Header: intl.formatMessage({ id: "report.action", defaultMessage: "Action" }),
        accessor: "id",
        id: "action",
        sortable: false,
        disableDrag: true,
        Cell: ({ row }: { row: { original: ScheduledReport } }) => (
          <p className="mb-0 d-flex align-items-center">
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 0 }}
              overlay={
                <Tooltip id={`edit-scheduled-report-${row.original.id}`}>
                  <span className="font-weight-normal">
                    <FormattedMessage id="general.edit" defaultMessage="Edit" />
                  </span>
                </Tooltip>
              }
            >
              <span className="d-inline-flex">
                <Button
                  variant="link"
                  className="pl-0 pr-0"
                  data-testid={testIds.ANALYTICS_SCHEDULED_REPORT_EDIT_BUTTON}
                  aria-label={intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" })}
                  onClick={() => {
                    setEditingReport(row.original);
                    setShowForm(true);
                  }}
                >
                  <EditPencil width={20} height={20} />
                </Button>
              </span>
            </OverlayTrigger>
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 0 }}
              overlay={
                <Tooltip id={`remove-scheduled-report-${row.original.id}`}>
                  <span className="font-weight-normal">
                    <FormattedMessage id="general.remove" defaultMessage="Remove" />
                  </span>
                </Tooltip>
              }
            >
              <span className="d-inline-flex">
                <Button
                  variant="link"
                  className="pl-0 pr-0 text-danger ml-2"
                  data-testid={testIds.ANALYTICS_SCHEDULED_REPORT_DELETE_BUTTON}
                  aria-label={intl.formatMessage({ id: "general.remove", defaultMessage: "Remove" })}
                  onClick={() => setDeleteId(row.original.id)}
                >
                  <RemoveIcon width={30} height={20} />
                </Button>
              </span>
            </OverlayTrigger>
          </p>
        ),
      },
    ],
    [intl],
  );

  const instance = useTable({ columns, data: reports });

  return (
    <>
      <ScheduledReportDialog
        show={showForm}
        report={editingReport}
        onHide={() => {
          setShowForm(false);
          setEditingReport(null);
        }}
        onSuccess={() => refetch()}
      />
      <ConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        title={intl.formatMessage({
          id: "report.deleteScheduledReport",
          defaultMessage: "Delete scheduled report",
        })}
        description={`${intl.formatMessage({
          id: "report.alertMessage",
          defaultMessage: "Are you sure you want to delete this report?",
        })} ${intl.formatMessage({
          id: "report.alertMessageUndone",
          defaultMessage: "This cannot be undone.",
        })}`}
        secondaryBtnText={intl.formatMessage({ id: "report.discard", defaultMessage: "Discard" })}
        submitBtnText={intl.formatMessage({ id: "report.submitDialog", defaultMessage: "Yes, I'm sure" })}
        onSecondaryBtnClick={() => setDeleteId(null)}
        onSubmit={handleDelete}
        submitTestId={testIds.ANALYTICS_SCHEDULED_REPORT_DELETE_CONFIRM_BUTTON}
      />
      <div className="scheduled-reports" data-testid={testIds.ANALYTICS_SCHEDULED_REPORT_TABLE}>
        <DataTable
          headerLeft={
            <h5 className="mb-0 mt-3">
              <FormattedMessage id="report.scheduledReports" defaultMessage="Scheduled Reports" />
            </h5>
          }
          headerRight={
            <Button
              className="rounded-pill mt-3"
              data-testid={testIds.ANALYTICS_SCHEDULED_REPORT_NEW_BUTTON}
              onClick={() => {
                setEditingReport(null);
                setShowForm(true);
              }}
            >
              <FormattedMessage id="report.newScheduledReport" defaultMessage="New scheduled report" />
            </Button>
          }
          tableInstance={instance}
          isLoading={isLoading}
          draggable={false}
          customNoResults={intl.formatMessage({
            id: "report.noScheduledReports",
            defaultMessage: "No scheduled reports.",
          })}
        />
      </div>
    </>
  );
}

export default ScheduledReports;
